import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../constants/helpRouteMap";
import {
  createSegment,
  deleteSegment,
  estimateSegment,
  estimateSegmentRules,
  fetchAllTags,
  getContactFieldDefinitions,
  getSegment,
  getSegments,
  loadTagOptions,
  Segment,
  SegmentCondition,
  SegmentRules,
  Tag,
  updateSegment,
  ContactFieldDefinition,
} from "../../helpers/audience_helper";
import { getSends } from "../../helpers/sends_helper";
import { describeSegmentRules } from "../../helpers/segmentRules";

const PER_PAGE = 25;

type TagOption = { value: number; label: string };

type DraftCondition =
  | {
      type: "status";
      op: "eq";
      value: string;
    }
  | {
      type: "tag";
      op: "in" | "not_in";
      tag_ids: number[];
      tag_match: "and" | "or";
    }
  | {
      type: "field";
      slug: string;
      op: "eq";
      value: string;
    }
  | {
      type: "engagement";
      event: "open" | "click";
      op: "in_last_days" | "not_in_last_days";
      days: number;
      campaign_send_id?: number | null;
    };

const defaultCondition = (): DraftCondition => ({
  type: "status",
  op: "eq",
  value: "active",
});

function toDraft(c: SegmentCondition): DraftCondition {
  if (c.type === "tag") {
    return {
      type: "tag",
      op: c.op,
      tag_ids: c.tag_ids,
      tag_match: c.tag_match === "and" ? "and" : "or",
    };
  }
  if (c.type === "field") {
    return {
      type: "field",
      slug: c.slug,
      op: "eq",
      value: String(c.value ?? ""),
    };
  }
  if (c.type === "engagement") {
    return { ...c, campaign_send_id: c.campaign_send_id ?? null };
  }
  return { type: "status", op: "eq", value: c.value };
}

function toApiCondition(c: DraftCondition): SegmentCondition | null {
  if (c.type === "status") {
    if (!c.value) return null;
    return { type: "status", op: "eq", value: c.value };
  }
  if (c.type === "tag") {
    if (c.tag_ids.length === 0) return null;
    return {
      type: "tag",
      op: c.op,
      tag_ids: c.tag_ids,
      ...(c.op === "in" ? { tag_match: c.tag_match } : {}),
    };
  }
  if (c.type === "field") {
    if (!c.slug || c.value === "") return null;
    return { type: "field", slug: c.slug, op: "eq", value: c.value };
  }
  if (c.days < 1 || c.days > 365) return null;
  const out: SegmentCondition = {
    type: "engagement",
    event: c.event,
    op: c.op,
    days: c.days,
  };
  if (c.campaign_send_id && c.campaign_send_id > 0) {
    out.campaign_send_id = c.campaign_send_id;
  }
  return out;
}

/**
 * Segmentos salvos F2 — regras reutilizáveis no disparo (DOC 03 § 4.8).
 */
const SegmentsList = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  document.title = t("segments.documentTitle");
  const helpHref = helpUrlForRoute("/segments", i18n.language || "pt-BR");

  const deepLinkHandled = useRef(false);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  const [segments, setSegments] = useState<Segment[]>([]);
  const [counts, setCounts] = useState<Record<number, number | null>>({});
  const [countsLoading, setCountsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Segment | null>(null);
  const [name, setName] = useState("");
  const [match, setMatch] = useState<"all" | "any">("all");
  const [conditions, setConditions] = useState<DraftCondition[]>([
    defaultCondition(),
  ]);
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [estimateCount, setEstimateCount] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);

  const [deleting, setDeleting] = useState<Segment | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);

  const [tagOptions, setTagOptions] = useState<TagOption[]>([]);
  const [fieldDefs, setFieldDefs] = useState<ContactFieldDefinition[]>([]);
  const [sendOptions, setSendOptions] = useState<
    Array<{ value: number; label: string }>
  >([]);

  const tagNames = useMemo(() => {
    const map: Record<number, string> = {};
    tagOptions.forEach((opt) => {
      map[opt.value] = opt.label;
    });
    return map;
  }, [tagOptions]);

  const fieldLabels = useMemo(() => {
    const map: Record<string, string> = {};
    fieldDefs.forEach((d) => {
      map[d.slug] = d.label;
    });
    return map;
  }, [fieldDefs]);

  const loadCatalogs = useCallback(async () => {
    try {
      const [tags, fieldsRes, sendsRes] = await Promise.all([
        fetchAllTags(),
        getContactFieldDefinitions({ per_page: 100 }),
        getSends({ per_page: 25, page: 1 }),
      ]);
      setTagOptions(
        tags.map((tag: Tag) => ({ value: tag.id, label: tag.name }))
      );
      setFieldDefs(
        (fieldsRes.data || []).filter((d) => d.is_filterable)
      );
      setSendOptions(
        (sendsRes.data || []).map((send) => ({
          value: send.id,
          label: `#${send.id} · ${send.subject || send.campaign?.name || "—"}`,
        }))
      );
    } catch {
      // catálogo opcional — formulário ainda funciona com status/engajamento
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSegments({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
      });
      if (!res?.success) {
        setError(t("segments.error.load"));
        setSegments([]);
        return;
      }
      const lp = res.meta?.last_page ?? 1;
      setSegments(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(lp);
      setCounts({});
      if (page > lp && lp >= 1) {
        setPage(lp);
      }
    } catch (e: any) {
      setError(e?.message || t("segments.error.load"));
      setSegments([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, t]);

  // Contagem elegível da página atual (estimate em paralelo — máx. PER_PAGE).
  useEffect(() => {
    if (segments.length === 0) {
      return;
    }
    let cancelled = false;
    setCountsLoading(true);
    Promise.all(
      segments.map(async (segment) => {
        try {
          const res = await estimateSegment(segment.id);
          return [segment.id, res?.success ? res.data.count : null] as const;
        } catch {
          return [segment.id, null] as const;
        }
      })
    ).then((rows) => {
      if (cancelled) return;
      const next: Record<number, number | null> = {};
      rows.forEach(([id, count]) => {
        next[id] = count;
      });
      setCounts(next);
      setCountsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [segments]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) return prev;
        setPage(1);
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [search]);

  const openCreate = () => {
    setEditing(null);
    setName("");
    setMatch("all");
    setConditions([defaultCondition()]);
    setFieldError(null);
    setEstimateCount(null);
    setModalOpen(true);
  };

  const openEdit = (segment: Segment) => {
    setEditing(segment);
    setName(segment.name);
    setMatch(segment.rules?.match === "any" ? "any" : "all");
    setConditions(
      (segment.rules?.conditions || []).map(toDraft).length > 0
        ? (segment.rules?.conditions || []).map(toDraft)
        : [defaultCondition()]
    );
    setFieldError(null);
    setEstimateCount(null);
    setModalOpen(true);
  };

  // Deep-link ?edit= ou ?highlight= — abre editor após salvar segmento na higiene.
  useEffect(() => {
    const editParam = searchParams.get("edit");
    const highlightParam = searchParams.get("highlight");
    const rawId = editParam || highlightParam;
    if (!rawId || deepLinkHandled.current || loading) {
      return;
    }
    const id = Number(rawId);
    if (!Number.isFinite(id) || id <= 0) {
      return;
    }

    deepLinkHandled.current = true;
    if (highlightParam) {
      setHighlightId(id);
    }

    void (async () => {
      try {
        const res = await getSegment(id);
        if (res?.success && res.data) {
          openEdit(res.data);
        } else {
          setError(t("segments.deepLinkNotFound"));
        }
      } catch {
        setError(t("segments.deepLinkNotFound"));
      } finally {
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        next.delete("highlight");
        setSearchParams(next, { replace: true });
      }
    })();
  }, [loading, searchParams, setSearchParams, t]);

  const buildRules = (): SegmentRules | null => {
    const apiConditions: SegmentCondition[] = [];
    for (const c of conditions) {
      const normalized = toApiCondition(c);
      if (!normalized) {
        return null;
      }
      apiConditions.push(normalized);
    }
    if (apiConditions.length === 0) {
      return null;
    }
    return { version: 1, match, conditions: apiConditions };
  };

  const runEstimate = async () => {
    const rules = buildRules();
    if (!rules) {
      setFieldError(t("segments.error.incompleteConditions"));
      return;
    }
    setEstimating(true);
    setFieldError(null);
    try {
      const res = await estimateSegmentRules(rules);
      if (res?.success) {
        setEstimateCount(res.data.count);
      }
    } catch (e: any) {
      setFieldError(e?.message || t("segments.error.estimate"));
      setEstimateCount(null);
    } finally {
      setEstimating(false);
    }
  };

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setFieldError(t("segments.error.nameRequired"));
      return;
    }
    const rules = buildRules();
    if (!rules) {
      setFieldError(t("segments.error.incompleteConditions"));
      return;
    }
    setSaving(true);
    setFieldError(null);
    try {
      if (editing) {
        const res = await updateSegment(editing.id, { name: trimmed, rules });
        if (!res?.success) {
          setFieldError(t("segments.error.save"));
          return;
        }
        setSuccess(t("segments.success.updated"));
      } else {
        const res = await createSegment({ name: trimmed, rules });
        if (!res?.success) {
          setFieldError(t("segments.error.save"));
          return;
        }
        setSuccess(t("segments.success.created"));
      }
      setModalOpen(false);
      await load();
    } catch (e: any) {
      setFieldError(e?.message || t("segments.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await deleteSegment(deleting.id);
      setSuccess(t("segments.success.deleted"));
      setDeleting(null);
      await load();
    } catch (e: any) {
      setError(e?.message || t("segments.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const statusOptions = useMemo(
    () => [
      { value: "active", label: t("segments.status.active") },
      { value: "pending_doi", label: t("segments.status.pending_doi") },
      { value: "unsubscribed", label: t("segments.status.unsubscribed") },
      { value: "quarantine", label: t("segments.status.quarantine") },
      { value: "suppressed", label: t("segments.status.suppressed") },
    ],
    [t]
  );

  const updateCondition = (index: number, next: DraftCondition) => {
    setConditions((prev) => prev.map((c, i) => (i === index ? next : c)));
    setEstimateCount(null);
  };

  const summarize = (segment: Segment): string => {
    return describeSegmentRules(segment.rules, t, { tagNames, fieldLabels });
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("segments.title")}
          pageTitle={t("menu.audience")}
          actions={
            helpHref ? (
              <PageHelpLink href={helpHref} label={t("segments.help")} />
            ) : undefined
          }
        />
        <DismissiblePageTip tipId="segments-intro">
          {t("segments.tip")}
        </DismissiblePageTip>

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert color="success" toggle={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div className="d-flex gap-2 align-items-center flex-grow-1">
                  <Input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("segments.searchPlaceholder")}
                    style={{ maxWidth: 280 }}
                  />
                  <span className="text-muted small">
                    {t("segments.total", { count: total })}
                  </span>
                </div>
                <Button color="primary" onClick={openCreate}>
                  {t("segments.create")}
                </Button>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : segments.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <p className="mb-3">{t("segments.empty")}</p>
                    <Button color="primary" outline onClick={openCreate}>
                      {t("segments.create")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("segments.col.name")}</th>
                            <th>{t("segments.col.rules")}</th>
                            <th className="text-end">{t("segments.col.count")}</th>
                            <th className="text-end">
                              {t("segments.col.actions")}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {segments.map((segment) => (
                            <tr
                              key={segment.id}
                              className={
                                highlightId === segment.id ? "table-info" : undefined
                              }
                            >
                              <td className="fw-medium">{segment.name}</td>
                              <td className="text-muted small">
                                {summarize(segment)}
                              </td>
                              <td className="text-end text-nowrap">
                                {countsLoading && counts[segment.id] === undefined ? (
                                  <Spinner size="sm" />
                                ) : counts[segment.id] != null ? (
                                  <Badge color="soft-primary" pill>
                                    {t("segments.countBadge", {
                                      count: counts[segment.id],
                                    })}
                                  </Badge>
                                ) : (
                                  <span className="text-muted">—</span>
                                )}
                              </td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  onClick={() => openEdit(segment)}
                                >
                                  {t("segments.edit")}
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  onClick={() => setDeleting(segment)}
                                >
                                  {t("segments.delete")}
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <Button
                          color="light"
                          size="sm"
                          disabled={page <= 1}
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                        >
                          {t("segments.prev")}
                        </Button>
                        <span className="text-muted small">
                          {page} / {lastPage}
                        </span>
                        <Button
                          color="light"
                          size="sm"
                          disabled={page >= lastPage}
                          onClick={() =>
                            setPage((p) => Math.min(lastPage, p + 1))
                          }
                        >
                          {t("segments.next")}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={modalOpen} toggle={() => !saving && setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => !saving && setModalOpen(false)}>
          {editing ? t("segments.modal.editTitle") : t("segments.modal.createTitle")}
        </ModalHeader>
        <ModalBody>
          <div className="mb-3">
            <Label>{t("segments.modal.name")}</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={saving}
              maxLength={120}
            />
          </div>
          <div className="mb-3">
            <Label>{t("segments.modal.match")}</Label>
            <Input
              type="select"
              value={match}
              disabled={saving}
              onChange={(e) => {
                setMatch(e.target.value === "any" ? "any" : "all");
                setEstimateCount(null);
              }}
            >
              <option value="all">{t("segments.matchAll")}</option>
              <option value="any">{t("segments.matchAny")}</option>
            </Input>
          </div>

          <Label className="mb-2">{t("segments.modal.conditions")}</Label>
          {conditions.length > 0 && (
            <Alert color="light" className="py-2 small border mb-3">
              <strong className="d-block mb-1">
                {t("segments.modal.preview")}
              </strong>
              {describeSegmentRules(
                {
                  version: 1,
                  match,
                  conditions: conditions
                    .map((c) => toApiCondition(c))
                    .filter(Boolean) as SegmentCondition[],
                },
                t,
                { tagNames, fieldLabels }
              )}
            </Alert>
          )}
          {conditions.map((condition, index) => (
            <Card key={index} className="mb-2 border">
              <CardBody className="py-3">
                <Row className="g-2 align-items-end">
                  <Col md={3}>
                    <Label className="small">{t("segments.modal.type")}</Label>
                    <Input
                      type="select"
                      value={condition.type}
                      disabled={saving}
                      onChange={(e) => {
                        const type = e.target.value;
                        if (type === "status") {
                          updateCondition(index, defaultCondition());
                        } else if (type === "tag") {
                          updateCondition(index, {
                            type: "tag",
                            op: "in",
                            tag_ids: [],
                            tag_match: "or",
                          });
                        } else if (type === "field") {
                          updateCondition(index, {
                            type: "field",
                            slug: fieldDefs[0]?.slug || "",
                            op: "eq",
                            value: "",
                          });
                        } else {
                          updateCondition(index, {
                            type: "engagement",
                            event: "open",
                            op: "in_last_days",
                            days: 30,
                            campaign_send_id: null,
                          });
                        }
                      }}
                    >
                      <option value="status">{t("segments.type.status")}</option>
                      <option value="tag">{t("segments.type.tag")}</option>
                      <option value="field">{t("segments.type.field")}</option>
                      <option value="engagement">
                        {t("segments.type.engagement")}
                      </option>
                    </Input>
                  </Col>

                  {condition.type === "status" && (
                    <Col md={6}>
                      <Label className="small">{t("segments.modal.value")}</Label>
                      <Input
                        type="select"
                        value={condition.value}
                        disabled={saving}
                        onChange={(e) =>
                          updateCondition(index, {
                            ...condition,
                            value: e.target.value,
                          })
                        }
                      >
                        {statusOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                  )}

                  {condition.type === "tag" && (
                    <>
                      <Col md={2}>
                        <Label className="small">{t("segments.modal.op")}</Label>
                        <Input
                          type="select"
                          value={condition.op}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              op: e.target.value === "not_in" ? "not_in" : "in",
                            })
                          }
                        >
                          <option value="in">{t("segments.op.tagIn")}</option>
                          <option value="not_in">
                            {t("segments.op.tagNotIn")}
                          </option>
                        </Input>
                      </Col>
                      <Col md={5}>
                        <Label className="small">{t("segments.modal.tags")}</Label>
                        <AsyncSelect
                          isMulti
                          cacheOptions
                          defaultOptions={tagOptions.length ? tagOptions : true}
                          loadOptions={loadTagOptions}
                          value={tagOptions.filter((o) =>
                            condition.tag_ids.includes(o.value)
                          )}
                          onChange={(opts: readonly TagOption[] | null) =>
                            updateCondition(index, {
                              ...condition,
                              tag_ids: (opts || []).map((o) => o.value),
                            })
                          }
                          isDisabled={saving}
                          classNamePrefix="select"
                          placeholder={t("segments.modal.tagsPlaceholder")}
                        />
                      </Col>
                    </>
                  )}

                  {condition.type === "field" && (
                    <>
                      <Col md={4}>
                        <Label className="small">{t("segments.modal.field")}</Label>
                        <Input
                          type="select"
                          value={condition.slug}
                          disabled={saving || fieldDefs.length === 0}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              slug: e.target.value,
                            })
                          }
                        >
                          {fieldDefs.length === 0 && (
                            <option value="">
                              {t("segments.modal.noFields")}
                            </option>
                          )}
                          {fieldDefs.map((d) => (
                            <option key={d.id} value={d.slug}>
                              {d.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                      <Col md={3}>
                        <Label className="small">{t("segments.modal.value")}</Label>
                        <Input
                          value={condition.value}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              value: e.target.value,
                            })
                          }
                        />
                      </Col>
                    </>
                  )}

                  {condition.type === "engagement" && (
                    <>
                      <Col md={2}>
                        <Label className="small">{t("segments.modal.event")}</Label>
                        <Input
                          type="select"
                          value={condition.event}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              event:
                                e.target.value === "click" ? "click" : "open",
                            })
                          }
                        >
                          <option value="open">{t("segments.event.open")}</option>
                          <option value="click">
                            {t("segments.event.click")}
                          </option>
                        </Input>
                      </Col>
                      <Col md={3}>
                        <Label className="small">{t("segments.modal.op")}</Label>
                        <Input
                          type="select"
                          value={condition.op}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              op:
                                e.target.value === "not_in_last_days"
                                  ? "not_in_last_days"
                                  : "in_last_days",
                            })
                          }
                        >
                          <option value="in_last_days">
                            {t("segments.op.inLastDays")}
                          </option>
                          <option value="not_in_last_days">
                            {t("segments.op.notInLastDays")}
                          </option>
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label className="small">{t("segments.modal.days")}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={365}
                          value={condition.days}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              days: Number(e.target.value) || 1,
                            })
                          }
                        />
                      </Col>
                      <Col md={5}>
                        <Label className="small">
                          {t("segments.modal.campaignSend")}
                        </Label>
                        <Input
                          type="select"
                          value={condition.campaign_send_id ?? ""}
                          disabled={saving}
                          onChange={(e) =>
                            updateCondition(index, {
                              ...condition,
                              campaign_send_id:
                                e.target.value === ""
                                  ? null
                                  : Number(e.target.value),
                            })
                          }
                        >
                          <option value="">
                            {t("segments.modal.campaignSendAny")}
                          </option>
                          {sendOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </Input>
                      </Col>
                    </>
                  )}

                  <Col md={2} className="text-end">
                    <Button
                      color="soft-danger"
                      size="sm"
                      disabled={saving || conditions.length <= 1}
                      onClick={() => {
                        setConditions((prev) =>
                          prev.filter((_, i) => i !== index)
                        );
                        setEstimateCount(null);
                      }}
                    >
                      {t("segments.removeCondition")}
                    </Button>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          ))}

          <Button
            color="light"
            size="sm"
            className="mb-3"
            disabled={saving || conditions.length >= 20}
            onClick={() => {
              setConditions((prev) => [...prev, defaultCondition()]);
              setEstimateCount(null);
            }}
          >
            {t("segments.addCondition")}
          </Button>

          <div className="d-flex align-items-center gap-2 mb-2">
            <Button
              color="soft-info"
              size="sm"
              disabled={saving || estimating}
              onClick={runEstimate}
            >
              {estimating ? (
                <Spinner size="sm" />
              ) : (
                t("segments.estimate")
              )}
            </Button>
            {estimateCount !== null && (
              <span className="text-muted small">
                {t("segments.estimateResult", { count: estimateCount })}
              </span>
            )}
          </div>

          {fieldError && (
            <FormFeedback className="d-block">{fieldError}</FormFeedback>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={saving}
            onClick={() => setModalOpen(false)}
          >
            {t("segments.cancel")}
          </Button>
          <Button color="primary" disabled={saving} onClick={save}>
            {saving ? <Spinner size="sm" /> : t("segments.save")}
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("segments.deleteTitle")}
        </ModalHeader>
        <ModalBody>
          {t("segments.deleteConfirm", { name: deleting?.name })}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("segments.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={confirmDelete}
          >
            {deletingBusy ? <Spinner size="sm" /> : t("segments.delete")}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SegmentsList;
